import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/navigation/AdminHeader';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import ProtectedRoute from '../../components/navigation/ProtectedRoute';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FilterSidebar from './components/FilterSidebar';
import BulkOperationsToolbar from './components/BulkOperationsToolbar';
import SearchBar from './components/SearchBar';
import UnitsTable from './components/UnitsTable';
import UnitDetailPanel from './components/UnitDetailPanel';
import AddEditUnitModal from './components/AddEditUnitModal';
import { supabase } from '../../lib/supabase';

const UnitsManagementSystem = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [yards, setYards] = useState([]);
  const [filters, setFilters] = useState({
    unitType: 'all',
    status: 'all',
    yard: 'all',
    onlyAssigned: false,
    needsMaintenance: false,
    highMileage: false
  });

  const [units, setUnits] = useState([]);
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      setUser({ email: 'admin@rpmdiesel.com' });
    }
    fetchYards();
    fetchUnits();
  }, []);

  const fetchYards = async () => {
    try {
      const { data, error } = await supabase?.from('yards')?.select('id, name')?.order('name');
      if (!error && data) {
        setYards(data);
      }
    } catch (err) {
      console.error('Error fetching yards:', err);
    }
  };

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('units')
        ?.select(`
          id, unit_number, vin, make, model, year, engine, esn, is_active, unit_type, status, yard_id,
          yards(name),
          driver_units!left(
            id, is_active,
            drivers(id, first_name, last_name)
          )
        `)
        ?.order('unit_number');
      if (!error && data) {
        const mapped = data?.map(u => {
          const activeAssignment = u?.driver_units?.find(du => du?.is_active !== false);
          const driver = activeAssignment?.drivers;
          const assignedDriverName = driver
            ? `${driver?.first_name || ''} ${driver?.last_name || ''}`?.trim()
            : null;
          return {
            id: u?.id,
            unitNumber: u?.unit_number,
            vin: u?.vin,
            make: u?.make,
            model: u?.model,
            year: u?.year,
            engine: u?.engine || '',
            esn: u?.esn || '',
            isActive: u?.is_active,
            unitType: u?.unit_type || null,
            status: u?.status || null,
            mileage: null,
            yardId: u?.yard_id || null,
            yard: u?.yards?.name || '',
            assignment: assignedDriverName,
            assignmentRole: assignedDriverName ? 'Driver' : null,
            assignmentHistory: [],
            maintenanceRecords: []
          };
        });
        setUnits(mapped);
        setFilteredUnits(mapped);
      }
    } catch (err) {
      console.error('Error fetching units:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [filters, searchQuery, units]);

  const applyFilters = () => {
    let filtered = [...units];

    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(unit =>
        unit?.unitNumber?.toLowerCase()?.includes(query) ||
        unit?.vin?.toLowerCase()?.includes(query) ||
        unit?.make?.toLowerCase()?.includes(query) ||
        unit?.model?.toLowerCase()?.includes(query)
      );
    }

    if (filters?.unitType !== 'all') {
      filtered = filtered?.filter(unit => unit?.unitType === filters?.unitType);
    }

    if (filters?.status !== 'all') {
      filtered = filtered?.filter(unit => unit?.status === filters?.status);
    }

    if (filters?.yard !== 'all') {
      filtered = filtered?.filter(unit => unit?.yardId === filters?.yard);
    }

    if (filters?.onlyAssigned) {
      filtered = filtered?.filter(unit => unit?.assignment !== null);
    }

    if (filters?.needsMaintenance) {
      filtered = filtered?.filter(unit => unit?.status === 'maintenance');
    }

    if (filters?.highMileage) {
      filtered = filtered?.filter(unit => unit?.mileage > 500000);
    }

    setFilteredUnits(filtered);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('sessionExpiry');
    navigate('/admin-authentication-portal');
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      unitType: 'all',
      status: 'all',
      yard: 'all',
      onlyAssigned: false,
      needsMaintenance: false,
      highMileage: false
    });
  };

  const handleSelectUnit = (unitId) => {
    setSelectedUnits(prev =>
      prev?.includes(unitId)
        ? prev?.filter(id => id !== unitId)
        : [...prev, unitId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUnits(filteredUnits?.map(unit => unit?.id));
    } else {
      setSelectedUnits([]);
    }
  };

  const handleViewDetails = (unit) => {
    setSelectedUnit(unit);
    setShowDetailPanel(true);
  };

  const handleEditUnit = (unit) => {
    setEditingUnit(unit);
    setShowAddEditModal(true);
    setShowDetailPanel(false);
  };

  const handleAddUnit = () => {
    setEditingUnit(null);
    setShowAddEditModal(true);
  };

  const handleSaveUnit = async (unitData) => {
    const dbData = {
      unit_number: unitData?.unitNumber,
      vin: unitData?.vin,
      make: unitData?.make || null,
      model: unitData?.model || null,
      year: unitData?.year || null,
      engine: unitData?.engine || null,
      esn: unitData?.esn || null,
      unit_type: unitData?.unitType || null,
      status: unitData?.status || null,
      yard_id: unitData?.yardId || null
    };

    if (editingUnit) {
      const { error } = await supabase?.from('units')?.update(dbData)?.eq('id', editingUnit?.id);
      if (error) {
        throw new Error(`Failed to update unit: ${error?.message}`);
      }
    } else {
      const { error } = await supabase?.from('units')?.insert([dbData]);
      if (error) {
        throw new Error(`Failed to create unit: ${error?.message}`);
      }
    }
    await fetchUnits();
    setShowAddEditModal(false);
    setEditingUnit(null);
  };

  const handleExport = () => {
    const headers = ['Unit Number', 'VIN', 'Make', 'Model', 'Year', 'Unit Type', 'Status', 'Yard', 'Mileage', 'Engine', 'ESN'];
    const rows = filteredUnits?.map(unit => [
      unit?.unitNumber || '',
      unit?.vin || '',
      unit?.make || '',
      unit?.model || '',
      unit?.year || '',
      unit?.unitType || '',
      unit?.status || '',
      unit?.yard || '',
      unit?.mileage || '',
      unit?.engine || '',
      unit?.esn || ''
    ]);

    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => row?.map(cell => `"${String(cell)?.replace(/"/g, '""')}"`)?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL?.createObjectURL(blob);
    const link = document?.createElement('a');
    link.href = url;
    link.download = `units-export-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    document?.body?.appendChild(link);
    link?.click();
    document?.body?.removeChild(link);
    URL?.revokeObjectURL(url);
  };

  const handleBulkStatusUpdate = () => {
    console.log('Bulk status update for units:', selectedUnits);
  };

  const handleBulkYardTransfer = () => {
    console.log('Bulk yard transfer for units:', selectedUnits);
  };

  const handleBulkMaintenanceSchedule = () => {
    console.log('Bulk maintenance schedule for units:', selectedUnits);
  };

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-background">
        <AdminHeader user={user} onLogout={handleLogout} />
        <AdminNavigation />

        <BulkOperationsToolbar
          selectedCount={selectedUnits?.length}
          onClearSelection={() => setSelectedUnits([])}
          onBulkStatusUpdate={handleBulkStatusUpdate}
          onBulkYardTransfer={handleBulkYardTransfer}
          onBulkMaintenanceSchedule={handleBulkMaintenanceSchedule}
        />

        <SearchBar
          onSearch={setSearchQuery}
          onAddUnit={handleAddUnit}
          onToggleFilters={() => setFilterSidebarOpen(!filterSidebarOpen)}
        />

        <div className="flex">
          <FilterSidebar
            isOpen={filterSidebarOpen}
            onClose={() => setFilterSidebarOpen(false)}
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            yards={yards}
          />

          <main className="flex-1 p-6">
            <div className="container mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl font-semibold text-foreground">Units Management</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Showing {filteredUnits?.length} of {units?.length} units
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Clock" size={16} strokeWidth={2} />
                    <span>Last updated: {new Date()?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Icon name="Loader2" size={32} strokeWidth={2} className="text-primary animate-spin" />
                </div>
              ) : filteredUnits?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 bg-card border border-border rounded-lg">
                  <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                    <Icon name="Truck" size={32} strokeWidth={2} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No units found</h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    {searchQuery || Object.values(filters)?.some(v => v !== 'all' && v !== false)
                      ? 'Try adjusting your search or filters' : 'Get started by adding your first unit'}
                  </p>
                  <button
                    onClick={handleAddUnit}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-hover"
                  >
                    <Icon name="Plus" size={18} strokeWidth={2} />
                    <span>Add Unit</span>
                  </button>
                </div>
              ) : (
                <UnitsTable
                  units={filteredUnits}
                  selectedUnits={selectedUnits}
                  onSelectUnit={handleSelectUnit}
                  onSelectAll={handleSelectAll}
                  onViewDetails={handleViewDetails}
                  onEditUnit={handleEditUnit}
                />
              )}
            </div>
          </main>
        </div>

        {showDetailPanel && (
          <UnitDetailPanel
            unit={selectedUnit}
            onClose={() => setShowDetailPanel(false)}
            onEdit={handleEditUnit}
          />
        )}

        {showAddEditModal && (
          <AddEditUnitModal
            isOpen={showAddEditModal}
            onClose={() => {
              setShowAddEditModal(false);
              setEditingUnit(null);
            }}
            unit={editingUnit}
            onSave={handleSaveUnit}
            yards={yards}
          />
        )}
      </div>
    </ProtectedRoute>
  );
};

export default UnitsManagementSystem;